import { FOV_TAN_HALF } from "../reelConstants";
import { ReelMediaItem } from "./reelMedia";

/**
 * How a reel opens.
 *
 * `classic` is the title card over the fall from the globe that every reel has always had. The other
 * two open on a real clip -- a curated one for marketing, or the traveller's own -- and differ only in
 * how that clip hands over to the map:
 *
 *   route-cut  the trip's route is drawn over the clip, then the clip splits open along that line and
 *              the map it was hiding is already there, route and all, before the camera dives in.
 *   clip-pin   the clip shrinks into a circle that lands on the first stop and becomes its pin.
 *   globe-bubble  a round window opens on the clip with the globe in it; the globe turns to the trip
 *              and zooms in, the stops come out around it, and the window swells to the whole frame.
 *   card-flip  the clip shrinks into one of the map's rounded photo cards, which turns over: its back
 *              is the map, with the route on it, and it grows to the whole frame.
 *   circle-zoom  the map first, with a circle drawn round the first stop by hand; the camera zooms
 *              into the circle, and the circle becomes a window onto the clip -- what you see there.
 *   circle-match  the clip first, with a circle drawn round its subject; the film falls away and the
 *              same circle is on the map round the first stop, which then zooms out to the trip.
 *   circle-dive   no clip: the map straight down on the first stop's area, a circle drawn round it by
 *              hand with a hook line and an arrow ("we went here for the mountains"), then a dive
 *              through the circle into the tilted map, landing where the first stop begins.
 *   passport-flip  no clip: a passport drops onto the blurred map and opens; every stop is stamped on
 *              a page of its own -- its name, its date, its nights, how you got there -- and the page
 *              turns. Then the passport falls away and the camera dives.
 *   passport-photos  no clip: a quick cut through the stops' own photos with a stamp slammed on each,
 *              then every stamp together over the blurred map, and the dive.
 *   boarding-pass  no clip: a boarding pass for the flight to the first stop, scanned and torn; its
 *              plane takes off as the route's own icon tile and flies to the first stop, where the dive
 *              begins.
 *   ticket-stack  no clip: every leg a ticket in the style of how it travels, dropped on a pile, laid
 *              out top to bottom and the first one punched.
 *   where-to-next  no clip: a split-flap board asks WHERE TO NEXT?, the destination spins through the
 *              world like a slot machine and lands on the trip, then the board opens like one big flap
 *              onto the map.
 *   departure-board  no clip: the trip as a departures board, a stop a row with its date, how you get
 *              there, its nights and a remark; then the board tips back onto the map.
 */
export type IntroStyle =
	| "classic"
	| "route-cut"
	| "clip-pin"
	| "globe-bubble"
	| "card-flip"
	| "circle-zoom"
	| "circle-match"
	| "circle-dive"
	| "passport-flip"
	| "passport-photos"
	| "boarding-pass"
	| "ticket-stack"
	| "where-to-next"
	| "departure-board";

/** The styles drawn on the map itself: they run without a clip. */
const MAP_ONLY: IntroStyle[] = ["circle-dive", "passport-flip", "passport-photos", "boarding-pass", "ticket-stack", "where-to-next", "departure-board"];

export type IntroConfig = {
	style?: IntroStyle;
	clip?: ReelMediaItem;
	/** Big line. Falls back to the trip's name. */
	title?: string;
	/** Small line above it. Falls back to nights and stops. */
	kicker?: string;
	/**
	 * How the drawing on the film is dressed. `stippl` is the house style -- Poppins, pills, round
	 * photos with a round flag, Puffy icons, the route as a dotted line. `postcard` is the scrapbook
	 * version: polaroids with tape and a handwritten name, a tilted flag sticker. Default `stippl`.
	 */
	look?: "stippl" | "postcard";
	/**
	 * House style only: how a stop's photographs appear. `bubbles` is one round photo, the website's
	 * destination bubble; `cards` is the fan of rounded, white-bordered, tilted cards the map shows
	 * at a stop when a reel runs in `photoMode: "cards"`. Default `bubbles`.
	 */
	photos?: "bubbles" | "cards";
	/** House style only: the title block. See `ClipIntro` for what each one is. Default `stack`. */
	header?: IntroHeader;
	/**
	 * circle-match: where the clip's subject is, as fractions of the frame, so the circle goes round
	 * the temple and not round the sky. A curation fact about the clip, like `startAt`. Default the
	 * upper middle, where a portrait establishing shot usually puts its subject.
	 */
	circleAt?: { x: number; y: number };
	/** The handwritten word next to the circle. Defaults to the first stop's name. */
	circleLabel?: string;
	/** circle-dive: how wide the circled area is on the ground, in km. An island, a valley, an old town. Default 14. */
	circleKm?: number;
	/** circle-zoom, circle-match, circle-dive: the colour of the circle, the arrow and a handwritten note. Default the brand pink (circle-dive with `caps`: white). */
	circleColor?: string;
	/** circle-dive: the note in `handwritten` letters (default) or in white `caps`. */
	noteFont?: "handwritten" | "caps";
	/**
	 * circle-dive: how the circle is drawn. `scribble` one marker loop (default) · `double` gone round
	 * twice, the way a hand circles something it means · `dashed` a dotted ring with an X on the spot,
	 * the treasure map · `spotlight` everything outside the circle dimmed · `highlighter` a fat
	 * see-through yellow marker · `neon` a glowing ring.
	 */
	circleStyle?: "scribble" | "double" | "dashed" | "spotlight" | "highlighter" | "neon";
	/**
	 * circle-dive: land close on the first stop's landmark and circle it before pulling back to the
	 * trip's height. For the 3D map, where the Eiffel Tower or the Colosseum is a model worth seeing;
	 * off on satellite, where close up is only a blurrier photo. The height of the close-up in metres
	 * above sea level, like every camera height in the reel, or `true` for 600: on a landmark on high
	 * ground, add the ground's height (times the relief) or the camera lands inside the terrain.
	 */
	closeUp?: boolean | number;
	/**
	 * boarding-pass: the airport codes on the pass, "AMS" and "CAI", and the city the flight leaves
	 * from, "Amsterdam". Default HOME, the first stop's first three letters, and no city.
	 */
	from?: string;
	to?: string;
	fromCity?: string;
};

export type IntroHeader = "stack" | "accent" | "card" | "poster" | "stats";

export const CLASSIC_INTRO_FRAMES = 60;

const CLIP_INTRO_FRAMES: Record<Exclude<IntroStyle, "classic">, number> = {
	// Six and a half seconds: the film gets to breathe before anything is drawn on it, the route is
	// drawn at a pace a viewer can follow, there is a beat with every photo out, and the dive lands on
	// the first stop rather than near it.
	"route-cut": 192,
	"clip-pin": 90,
	"globe-bubble": 160,
	"card-flip": 176,
	"circle-zoom": 180,
	"circle-match": 176,
	// Two and a half seconds to read the hook, two to dive, a second and a half round the place.
	"circle-dive": 196,
	// Three and a half seconds of stamps, a beat, and the dive.
	"passport-flip": 160,
	"passport-photos": 160,
	// The pass, the tickets and the boards: three and a half seconds, then the dive.
	"boarding-pass": 160,
	"ticket-stack": 160,
	"where-to-next": 160,
	"departure-board": 160,
};

/**
 * Frames the intro's overlay keeps running into the first stop. The camera belongs to MarkerSequence
 * from INTRO onwards; only the intro's own pin is still on screen while the real marker springs in
 * underneath it, so the pin is handed over rather than swapped.
 */
const OVERHANG: Record<IntroStyle, number> = {
	classic: 0,
	"route-cut": 10,
	"clip-pin": 14,
	"globe-bubble": 10,
	"card-flip": 10,
	"circle-zoom": 14,
	"circle-match": 10,
	"circle-dive": 10,
	// Nothing of a passport's is left by the time the first stop starts: there is no pin to hand over.
	"passport-flip": 0,
	"passport-photos": 0,
	"boarding-pass": 0,
	"ticket-stack": 0,
	"where-to-next": 0,
	"departure-board": 0,
};
export const introOverhang = (style: IntroStyle) => OVERHANG[style];

/** Which intro actually runs. A clip style without a clip is the classic intro, not a blank one. */
export const introStyleOf = (styles: any): IntroStyle => {
	const intro = styles?.intro as IntroConfig | undefined;
	if (!intro?.style || intro.style === "classic") return "classic";
	if (!intro.clip && !MAP_ONLY.includes(intro.style)) return "classic";
	return intro.style;
};

/**
 * The intro's length. The ONE place it is decided: the component offsets every sequence by it and
 * `calculateReelDuration` adds it, and when those were two literal 60s a longer intro would have
 * silently pushed the outro past the end of the composition.
 */
export const introFrames = (styles: any): number => {
	const style = introStyleOf(styles);
	if (style === "classic") return CLASSIC_INTRO_FRAMES;
	// A close-up landing circles its landmark a little longer before pulling back to the trip.
	const closeUp = style === "circle-dive" && styles?.intro?.closeUp ? 24 : 0;
	return CLIP_INTRO_FRAMES[style] + closeUp;
};

type LngLat = { lng: number; lat: number };

/**
 * A straight-down camera that frames every point of the trip inside a band of the screen.
 *
 * `band` is where the route should sit, as fractions of the frame: the top of the frame belongs to
 * the title, and the bottom 400 px of a 1920 frame belongs to Instagram's caption and buttons.
 * Returned as the point the camera looks at plus its altitude, which is what the free camera takes.
 */
export const overviewCamera = (
	coords: number[][],
	aspect: number,
	band: { top: number; bottom: number; width: number; x?: number } = { top: 0.36, bottom: 0.8, width: 0.74 }
): { target: LngLat; altitude: number } | null => {
	const pts = (coords || []).filter((c) => Array.isArray(c) && isFinite(c[0]) && isFinite(c[1]));
	if (pts.length === 0) return null;
	const lngs = pts.map((c) => c[0]);
	const lats = pts.map((c) => c[1]);
	const lngMin = Math.min(...lngs);
	const lngMax = Math.max(...lngs);
	const latMin = Math.min(...lats);
	const latMax = Math.max(...lats);
	const latC = (latMin + latMax) / 2;
	const lngC = (lngMin + lngMax) / 2;

	const M_PER_DEG = 111320;
	const dy = Math.max((latMax - latMin) * M_PER_DEG, 2000);
	const dx = Math.max((lngMax - lngMin) * M_PER_DEG * Math.cos((latC * Math.PI) / 180), 2000);

	const bandH = band.bottom - band.top;
	// Visible height at altitude A, looking straight down, is 2 A tan(fov/2); width is that times aspect.
	const altitude = Math.max(dy / (bandH * 2 * FOV_TAN_HALF), dx / (band.width * 2 * FOV_TAN_HALF * aspect));

	// The band's middle sits below the frame's middle, so the camera looks at a point north of the
	// route's centre by that offset -- which pushes the route down the screen and out from under the title.
	const visibleH = 2 * altitude * FOV_TAN_HALF;
	const shift = ((band.top + band.bottom) / 2 - 0.5) * visibleH;
	// Sideways the same way: a band right of the middle (a landscape frame, words on the left) means
	// looking at a point west of the route, so the route lands in the band. The overview is north up.
	const shiftX = ((band.x ?? 0.5) - 0.5) * visibleH * aspect;
	return { target: { lng: lngC - shiftX / (M_PER_DEG * Math.cos((latC * Math.PI) / 180)), lat: latC + shift / M_PER_DEG }, altitude };
};

/** Fraction along a polyline at which each of its vertices sits, for dots that pop as the line reaches them. */
export const vertexFractions = (xy: number[][]): number[] => {
	if (xy.length < 2) return xy.map(() => 0);
	const seg = xy.slice(1).map((p, i) => Math.hypot(p[0] - xy[i][0], p[1] - xy[i][1]));
	const total = seg.reduce((a, b) => a + b, 0) || 1;
	let run = 0;
	return xy.map((_, i) => {
		if (i > 0) run += seg[i - 1];
		return run / total;
	});
};

/** Everything inside a bbox, as [lngMin, latMin, lngMax, latMax]. */
const bboxOf = (pts: number[][]) => {
	const lngs = pts.map((p) => p[0]);
	const lats = pts.map((p) => p[1]);
	return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
};

/**
 * The outlines of the countries a trip is in, as rings of [lng, lat] -- or nothing, when drawing them
 * would not help.
 *
 * The silhouette is what makes a line on a film read as a MAP: a thin stroke through a temple is a
 * scratch, the same stroke inside the shape of Thailand is a route. But it only earns that when the
 * route is a real part of the country. A Bangkok bar crawl inside the outline of Thailand is a dot in
 * a shape; past `maxRatio` the country is left out and the route is framed on its own.
 *
 * `shapes` is a FeatureCollection keyed by `properties.iso` (alpha-2), `isoOf` resolves a trip's
 * country name the way the rest of the reel does.
 */
export const countryRings = (
	countryNames: string[],
	route: number[][],
	shapes: { features: any[] },
	isoOf: (name: string) => string | undefined,
	maxRatio = 4
): number[][][] => {
	if (!route?.length || !shapes?.features?.length) return [];
	const isos = new Set(countryNames.map((n) => isoOf(n)?.toUpperCase()).filter(Boolean));
	const rings: number[][][] = [];
	shapes.features
		.filter((f) => isos.has(String(f?.properties?.iso || "").toUpperCase()))
		.forEach((f) => {
			const g = f.geometry;
			const polys = g?.type === "Polygon" ? [g.coordinates] : g?.type === "MultiPolygon" ? g.coordinates : [];
			polys.forEach((poly: number[][][]) => poly[0] && rings.push(poly[0]));
		});
	if (!rings.length) return [];

	const [rx0, ry0, rx1, ry1] = bboxOf(route);
	const [cx0, cy0, cx1, cy1] = bboxOf(rings.flat());
	const routeSpan = Math.max(rx1 - rx0, ry1 - ry0, 0.05);
	const countrySpan = Math.max(cx1 - cx0, cy1 - cy0);
	return countrySpan / routeSpan <= maxRatio ? rings : [];
};
